import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Link } from '@mui/material';
import { useGridApiRef, GridActionsCellItem } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import * as fileApi from '../../lib/api/file';
import AskDialog from '../../components/common/dialog/AskDialog';
import PdfViewerDialog from '../../components/common/dialog/PdfViewerDialog';
import EsgFileList from '../../components/esgmng/EsgFileList';
import { listEsgFiles, unloadEsgFiles } from '../../modules/esgModule/esgFiles';

const EsgFileListContainer = ({ board }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { files, loading } = useSelector(({ esgModule, loading }) => ({
    files: esgModule.esgFiles.files,
    loading: loading['esg/LIST_ESG_FILES'],
  }));

  useEffect(() => {
    if (board) {
      dispatch(listEsgFiles('esg', board.wrUuid));
    }
    return () => {
      dispatch(unloadEsgFiles());
    };
  }, [dispatch, board]);

  const apiRef = useGridApiRef();

  const handleDownload = async (fileUuid, fileNm) => {
    try {
      const response = await fileApi.downloadFile(fileUuid);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileNm;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      a.remove();
    } catch (err) {
      enqueueSnackbar('파일 다운로드에 실패했습니다.', { variant: 'error' });
    }
  };

  const [pdfViewer, setPdfViewer] = useState({ open: false, url: null, name: '' });

  const handleViewPdf = async (fileUuid, fileNm) => {
    try {
      const response = await fileApi.downloadFile(fileUuid);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPdfViewer({ open: true, url, name: fileNm });
    } catch (err) {
      enqueueSnackbar('파일을 불러오는데 실패했습니다.', { variant: 'error' });
    }
  };

  const handleClosePdf = () => {
    if (pdfViewer.url) window.URL.revokeObjectURL(pdfViewer.url);
    setPdfViewer({ open: false, url: null, name: '' });
  };

  const [removeId, setRemoveId] = useState(null);
  const [visible, setVisible] = useState(false);

  const handleDeleteClick = (id) => (event) => {
    event.stopPropagation();
    setRemoveId(id);
    setVisible(true);
  };

  const onRemove = async () => {
    try {
      await fileApi.removeFile(removeId);
      apiRef.current.updateRows([{ id: removeId, _action: 'delete' }]);
      enqueueSnackbar('삭제 성공', { variant: 'success' });
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      enqueueSnackbar(message, { variant: 'error' });
    }
    setRemoveId(null);
  };

  const onCancle = () => { setVisible(false); setRemoveId(null); };
  const onConfirm = () => { setVisible(false); onRemove(); };

  const columns = useMemo(
    () => [
      { field: 'id', hide: true },
      { field: 'fileUuid', hide: true },
      { field: 'fileTyp', hide: true },
      { field: 'fileAttrVal1', hide: true },
      {
        field: 'fileNm',
        headerName: '파일명',
        minWidth: 400,
        flex: 1,
        renderCell: (params) => {
          const isPdf = params.row.fileNm?.toLowerCase().endsWith('.pdf');
          return (
            <Link
              component="button"
              variant="body2"
              onClick={() =>
                isPdf
                  ? handleViewPdf(params.row.fileUuid, params.row.fileNm)
                  : handleDownload(params.row.fileUuid, params.row.fileNm)
              }
              sx={{ cursor: 'pointer', textAlign: 'left' }}
            >
              {params.value}
            </Link>
          );
        },
      },
      { field: 'fileDesc', headerName: '참고사항', minWidth: 300, flex: 1 },
      { field: 'crtNm', headerName: '등록자', width: 90 },
      { field: 'crtDt', headerName: '등록일', width: 160 },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: 100,
        getActions: ({ id, row }) => [
          <GridActionsCellItem
            icon={<CloudDownloadIcon />}
            label="Download"
            onClick={() => handleDownload(row.fileUuid, row.fileNm)}
            color="primary"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ],
      },
    ],
    [handleDownload],
  );

  return (
    <div>
      <EsgFileList
        apiRef={apiRef}
        columns={columns}
        files={files}
        loading={loading}
        columnVisibilityModel={{ id: false, fileUuid: false, fileTyp: false, fileAttrVal1: false }}
      />
      <PdfViewerDialog
        open={pdfViewer.open}
        url={pdfViewer.url}
        fileName={pdfViewer.name}
        onClose={handleClosePdf}
      />
      <AskDialog
        visible={visible}
        title="ESG 첨부파일 삭제"
        description="선택한 첨부 파일을 삭제하시겠습니까?"
        confirmText="삭제"
        onConfirm={onConfirm}
        onCancle={onCancle}
      />
    </div>
  );
};

export default EsgFileListContainer;
